import Link from 'next/link';
import { RespImage } from '@/app/_components/core/resp-image';
import { RichText } from '@/app/_components/core/rich-text';
import type { PortraitTeaserItem } from '@/app/_components/shared/portrait-teaser/portrait-teaser.types';
import { transformSrcsetCandidates } from '@/app/_lib/image-art-direction';
import { emptyToUndefined } from '@/app/_lib/utils';

// Image sizes for responsive 3:4 portrait images
// xl (1280px+, 4 cols): 290px | lg (1024px, 4 cols): 236px | md (768px, 2 cols): 360px | sm (640px, 1 col): 608px | mobile: full
const IMAGE_SIZES =
  '(min-width: 1280px) 290px, (min-width: 1024px) 236px, (min-width: 768px) 360px, (min-width: 640px) 608px, calc(100vw - 32px)';

interface PortraitTeaserCardProps {
  item: PortraitTeaserItem;
  priority?: boolean;
  trackingPrefix?: string;
}

export function PortraitTeaserCard({ item, priority, trackingPrefix }: PortraitTeaserCardProps) {
  const { srcsetCandidates } = item.image?.srcsetCandidates
    ? transformSrcsetCandidates(item.image.srcsetCandidates)
    : { srcsetCandidates: [] };

  const linkUrl = item.link?.linkUrl;
  const hasLink = !!linkUrl;

  const imageElement = srcsetCandidates.length > 0 ? (
    <div className="aspect-3/4 overflow-hidden rounded-portrait-teaser-media-radius">
      <RespImage
        srcsetCandidates={srcsetCandidates}
        alt={emptyToUndefined(item.image?.altText) ?? emptyToUndefined(item.headline) ?? ''}
        title={item.image?.title ?? undefined}
        sizes={IMAGE_SIZES}
        priority={priority}
        className="size-full object-cover"
      />
    </div>
  ) : (
    <div className="aspect-3/4 overflow-hidden rounded-portrait-teaser-media-radius bg-muted" />
  );

  return (
    <article className="flex h-full flex-col gap-4">
      {/* Image - linked if link is present */}
      {hasLink && linkUrl ? (
        <Link href={linkUrl} className="block" data-track-id={trackingPrefix}>
          {imageElement}
        </Link>
      ) : (
        imageElement
      )}

      {/* Content */}
      <div className="flex grow flex-col gap-1">
        {item.kicker && (
          <p className="text-sm text-muted-foreground">{item.kicker}</p>
        )}
        {item.headline && (
          <h3
            className="line-clamp-3 text-base font-medium text-foreground"
            title={item.headline}
          >
            {item.headline}
          </h3>
        )}
        {item.copytext && (
          <RichText className="mt-auto pt-2">
            {item.copytext}
          </RichText>
        )}
      </div>

    </article>
  );
}
